import { context, metrics } from '@opentelemetry/api'
import { type Logger, SeverityNumber, logs } from '@opentelemetry/api-logs'
import { ZoneContextManager } from '@opentelemetry/context-zone'
import { OTLPLogExporter } from '@opentelemetry/exporter-logs-otlp-http'
import { OTLPMetricExporter } from '@opentelemetry/exporter-metrics-otlp-http'
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http'
import { registerInstrumentations } from '@opentelemetry/instrumentation'
import { DocumentLoadInstrumentation } from '@opentelemetry/instrumentation-document-load'
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch'
import { resourceFromAttributes } from '@opentelemetry/resources'
import { BatchLogRecordProcessor, LoggerProvider } from '@opentelemetry/sdk-logs'
import { MeterProvider, PeriodicExportingMetricReader } from '@opentelemetry/sdk-metrics'
// BatchSpanProcessor comes from sdk-trace-web (it re-exports the sdk-trace-base
// surface) rather than importing sdk-trace-base directly, which is only a
// transitive dependency here and isn't type-resolvable as a direct import.
import { BatchSpanProcessor, WebTracerProvider } from '@opentelemetry/sdk-trace-web'
import { ATTR_SERVICE_NAME } from '@opentelemetry/semantic-conventions'
import { env } from './utils/env'

// api.myokami.xyz is a different origin than app.myokami.xyz, so the fetch
// instrumentation needs an explicit allowlist to attach the traceparent header
// on cross-origin requests. Matching is exact-string-or-regex (see
// shouldPropagateTraceHeaders in @opentelemetry/sdk-trace-web) — a plain origin
// string only matches a request with no path, so this must be a RegExp.
const API_TRACE_HEADER_URLS = [/^https:\/\/api\.myokami\.xyz/]

// @opentelemetry/instrumentation-console@0.2.0 (the only version published)
// calls `super.enable()`, but InstrumentationAbstract in the installed
// @opentelemetry/instrumentation@0.220.0 doesn't implement enable() — it's
// abstract. That threw "super.enable is not a function" straight out of
// registerInstrumentations, uncaught, which aborted main.tsx before the router
// or ReactDOM.render ever ran. Patching console by hand avoids that broken base
// class entirely; the mapping/re-entrancy-guard logic below mirrors what that
// package does internally.
const CONSOLE_METHODS: Record<string, { severityNumber: SeverityNumber; severityText: string }> = {
  trace: { severityNumber: SeverityNumber.TRACE, severityText: 'trace' },
  debug: { severityNumber: SeverityNumber.DEBUG, severityText: 'debug' },
  log: { severityNumber: SeverityNumber.INFO, severityText: 'info' },
  info: { severityNumber: SeverityNumber.INFO, severityText: 'info' },
  warn: { severityNumber: SeverityNumber.WARN, severityText: 'warn' },
  error: { severityNumber: SeverityNumber.ERROR, severityText: 'error' },
}

// Deliberately not util.format: that pulls in the "util" npm polyfill, which
// itself reads the Node `process` global at import time and throws
// "process is not defined" the instant it loads in a real browser — confirmed
// by actually running this in headless Chrome, not just building it. Console
// log bodies don't need printf-style substitution, so a plain stringifier
// avoids the whole Node-polyfill rabbit hole.
function formatConsoleArgs(args: unknown[]): string {
  return args
    .map((arg) => {
      if (typeof arg === 'string') return arg
      if (arg instanceof Error) return arg.stack || arg.message
      try {
        return JSON.stringify(arg)
      } catch {
        return String(arg)
      }
    })
    .join(' ')
}

function patchConsoleForLogs(logger: Logger) {
  let isEmitting = false

  const consoleRecord = console as unknown as Record<string, unknown>

  for (const [method, { severityNumber, severityText }] of Object.entries(CONSOLE_METHODS)) {
    const original = consoleRecord[method]
    if (typeof original !== 'function') continue

    consoleRecord[method] = (...args: unknown[]) => {
      // guards against the exporter itself logging to console mid-emit
      if (isEmitting) return original(...args)

      isEmitting = true
      try {
        const timestamp = Date.now()
        logger.emit({
          timestamp,
          observedTimestamp: timestamp,
          severityNumber,
          severityText,
          body: formatConsoleArgs(args),
          context: context.active(),
        })
      } finally {
        isEmitting = false
      }

      return original(...args)
    }
  }
}

export function initOtel() {
  // OTLP_URL is unset on local dev machines; without this guard every exporter
  // would be created with `undefined/v1/...` and every export would fail.
  if (!env.VITE_OTLP_URL) return

  // Telemetry must never be able to take the app down. A single misbehaving
  // instrumentation already did exactly that in production once (see above) —
  // this is the backstop for the next one.
  try {
    const resource = resourceFromAttributes({
      [ATTR_SERVICE_NAME]: 'okami-front-end',
    })

    const tracerProvider = new WebTracerProvider({
      resource,
      // OTel SDK v2 dropped provider.addSpanProcessor(); processors are now
      // constructor-only.
      spanProcessors: [
        new BatchSpanProcessor(new OTLPTraceExporter({ url: `${env.VITE_OTLP_URL}/v1/traces` })),
      ],
    })

    tracerProvider.register({
      contextManager: new ZoneContextManager(),
    })

    const loggerProvider = new LoggerProvider({
      resource,
      processors: [
        new BatchLogRecordProcessor({
          exporter: new OTLPLogExporter({ url: `${env.VITE_OTLP_URL}/v1/logs` }),
        }),
      ],
    })

    logs.setGlobalLoggerProvider(loggerProvider)
    patchConsoleForLogs(logs.getLogger('console'))

    const meterProvider = new MeterProvider({
      resource,
      readers: [
        new PeriodicExportingMetricReader({
          exporter: new OTLPMetricExporter({ url: `${env.VITE_OTLP_URL}/v1/metrics` }),
        }),
      ],
    })

    metrics.setGlobalMeterProvider(meterProvider)

    registerInstrumentations({
      tracerProvider,
      loggerProvider,
      meterProvider,
      instrumentations: [
        new DocumentLoadInstrumentation(),
        new FetchInstrumentation({
          propagateTraceHeaderCorsUrls: API_TRACE_HEADER_URLS,
        }),
      ],
    })
  } catch (error) {
    console.error('[otel] failed to initialize telemetry', error)
  }
}
